/**
 * smarq-motion.js
 * One small motion system for the Smarq theme:
 *   - scroll reveals via IntersectionObserver ([data-sq-reveal], staggered with --delay)
 *   - scroll-progress parallax ([data-sq-parallax="0.2"])
 *   - pointer-driven tilt / glow on tiles ([data-sq-tilt])
 *   - header state (.is-scrolled once the page moves)
 * Everything is gated by prefers-reduced-motion: reveals show instantly and
 * parallax/tilt are skipped. No dependencies. Re-runs on Shopify editor events.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var reduced = reduceQuery.matches;

  root.classList.add('sq-motion');

  /* ---------- Reveal ---------- */
  var revealObserver = null;

  function revealAll() {
    document.querySelectorAll('[data-sq-reveal]').forEach(function (el) {
      el.classList.add('is-in');
    });
  }

  function initReveal(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sq-reveal]:not(.is-in)');
    if (!nodes.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      nodes.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    }

    nodes.forEach(function (el) {
      // Anything already in view on load reveals immediately, so the first fold never waits.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) {
        el.classList.add('is-in');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ---------- Parallax ---------- */
  var parallaxItems = [];
  var ticking = false;

  function collectParallax() {
    parallaxItems = [];
    if (reduced) return;
    document.querySelectorAll('[data-sq-parallax]').forEach(function (el) {
      var strength = parseFloat(el.getAttribute('data-sq-parallax'));
      if (isNaN(strength)) strength = 0.15;
      parallaxItems.push({ el: el, strength: strength });
    });
  }

  function runParallax() {
    ticking = false;
    if (!parallaxItems.length) return;
    var vh = window.innerHeight;
    parallaxItems.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      // -1 when the element's centre is a viewport below, +1 when a viewport above
      var progress = ((rect.top + rect.height / 2) - vh / 2) / vh;
      var offset = -progress * item.strength * 100;
      item.el.style.transform = 'translate3d(0,' + offset.toFixed(2) + 'px,0)';
    });
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(runParallax);
    }
    updateHeader();
  }

  /* ---------- Header state ---------- */
  var header = null;
  function updateHeader() {
    if (!header) header = document.querySelector('[data-sq-header]');
    if (!header) return;
    var scrolled = (window.scrollY || window.pageYOffset) > 12;
    header.classList.toggle('is-scrolled', scrolled);
  }

  /* ---------- Tilt / glow ---------- */
  function initTilt(scope) {
    if (reduced) return;
    var fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;

    (scope || document).querySelectorAll('[data-sq-tilt]:not([data-sq-tilt-ready])').forEach(function (el) {
      el.setAttribute('data-sq-tilt-ready', '');
      var max = parseFloat(el.getAttribute('data-sq-tilt')) || 4;
      var frame = null;

      function move(e) {
        var rect = el.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(function () {
          el.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
          el.style.setProperty('--my', (py * 100).toFixed(1) + '%');
          el.style.setProperty('--rx', ((0.5 - py) * max).toFixed(2) + 'deg');
          el.style.setProperty('--ry', ((px - 0.5) * max).toFixed(2) + 'deg');
        });
      }
      function leave() {
        if (frame) cancelAnimationFrame(frame);
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      }
      el.addEventListener('pointermove', move, { passive: true });
      el.addEventListener('pointerleave', leave, { passive: true });
    });
  }

  /* ---------- Mobile nav drawer: close on link tap / escape ---------- */
  function initDrawer() {
    var drawer = document.querySelector('[data-sq-drawer]');
    if (!drawer || drawer.hasAttribute('data-sq-drawer-ready')) return;
    drawer.setAttribute('data-sq-drawer-ready', '');
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('a')) drawer.removeAttribute('open');
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.hasAttribute('open')) {
        drawer.removeAttribute('open');
        var summary = drawer.querySelector('summary');
        if (summary) summary.focus();
      }
    });
    drawer.addEventListener('toggle', function () {
      document.body.classList.toggle('sq-drawer-open', drawer.hasAttribute('open'));
    });
  }

  /* ---------- Boot ---------- */
  function init(scope) {
    initReveal(scope);
    collectParallax();
    initTilt(scope);
    initDrawer();
    updateHeader();
    runParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () { collectParallax(); runParallax(); }, { passive: true });

  if (reduceQuery.addEventListener) {
    reduceQuery.addEventListener('change', function (e) {
      reduced = e.matches;
      if (reduced) {
        revealAll();
        parallaxItems.forEach(function (i) { i.el.style.transform = ''; });
        parallaxItems = [];
      }
    });
  }

  // Shopify theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    if (el && el.hasAttribute && el.hasAttribute('data-sq-reveal')) el.classList.add('is-in');
  });

  window.SmarqMotion = { refresh: init, revealAll: revealAll };
})();
